/**
 * ASCII Art Generator - Noise 噪点效果 (Film Grain)
 *
 * 静态颗粒噪点，类似老照片胶片质感
 */

import type { NoiseConfig } from './types'
import { clamp } from './helpers'

// ============================================
// 噪点生成
// ============================================

/**
 * 生成单个噪点值
 * @returns -0.5 到 0.5 的随机值
 */
function generateNoiseValue(): number {
  return Math.random() - 0.5
}

/**
 * 生成噪点纹理数据
 * @param width 宽度
 * @param height 高度
 * @param scale 颗粒大小
 * @param density 密度
 * @param colored 是否彩色
 * @returns 噪点 ImageData
 */
export function generateNoiseTexture(
  width: number,
  height: number,
  scale: number,
  density: number,
  colored: boolean
): ImageData {
  const data = new Uint8ClampedArray(width * height * 4)

  // 根据密度决定噪点分布
  const threshold = density

  for (let y = 0; y < height; y += scale) {
    for (let x = 0; x < width; x += scale) {
      // 是否在此位置生成噪点
      if (Math.random() > threshold) {
        continue
      }

      // 生成噪点值
      const noiseValue = generateNoiseValue()

      // 填充颗粒区域
      for (let dy = 0; dy < scale && y + dy < height; dy++) {
        for (let dx = 0; dx < scale && x + dx < width; dx++) {
          const i = ((y + dy) * width + (x + dx)) * 4

          if (colored) {
            // 彩色噪点：每个通道独立随机
            data[i] = clamp(128 + generateNoiseValue() * 128, 0, 255)     // R
            data[i + 1] = clamp(128 + generateNoiseValue() * 128, 0, 255) // G
            data[i + 2] = clamp(128 + generateNoiseValue() * 128, 0, 255) // B
          } else {
            // 灰度噪点：所有通道相同
            const grayValue = clamp(128 + noiseValue * 128, 0, 255)
            data[i] = grayValue
            data[i + 1] = grayValue
            data[i + 2] = grayValue
          }
          data[i + 3] = 255 // Alpha
        }
      }
    }
  }

  return new ImageData(data, width, height)
}

// ============================================
// Canvas 应用函数
// ============================================

/**
 * 应用噪点效果到 Canvas
 * @param canvas 目标画布
 * @param config 噪点配置
 */
export function applyNoise(
  canvas: HTMLCanvasElement,
  config: NoiseConfig
): void {
  const ctx = canvas.getContext('2d')!
  const { width, height } = canvas
  const { strength, scale, density, colored } = config

  // 获取当前画布内容
  const originalData = ctx.getImageData(0, 0, width, height)
  const originalPixels = originalData.data

  // 生成噪点纹理
  const noiseTexture = generateNoiseTexture(width, height, scale, density, colored)
  const noisePixels = noiseTexture.data

  // 混合噪点
  for (let i = 0; i < originalPixels.length; i += 4) {
    // 噪点强度影响
    const noiseR = noisePixels[i] - 128
    const noiseG = noisePixels[i + 1] - 128
    const noiseB = noisePixels[i + 2] - 128

    // 应用噪点到原图 - 提高强度系数使噪点更明显
    originalPixels[i] = clamp(originalPixels[i] + noiseR * strength * 3, 0, 255)
    originalPixels[i + 1] = clamp(originalPixels[i + 1] + noiseG * strength * 3, 0, 255)
    originalPixels[i + 2] = clamp(originalPixels[i + 2] + noiseB * strength * 3, 0, 255)
  }

  // 写回画布
  ctx.putImageData(originalData, 0, 0)
}

// ============================================
// 类封装（用于动画）
// ============================================

/**
 * 噪点效果类
 * 用于管理噪点状态和动画
 */
export class NoiseEffect {
  private config: NoiseConfig
  private noiseTexture: ImageData | null = null
  private lastWidth: number = 0
  private lastHeight: number = 0

  constructor(config: NoiseConfig) {
    this.config = config
  }

  /**
   * 更新配置
   */
  updateConfig(config: NoiseConfig): void {
    // 如果关键参数变化，重新生成噪点纹理
    if (
      config.scale !== this.config.scale ||
      config.density !== this.config.density ||
      config.colored !== this.config.colored
    ) {
      this.noiseTexture = null
    }
    this.config = config
  }

  /**
   * 应用效果
   */
  apply(canvas: HTMLCanvasElement): void {
    const { width, height } = canvas

    // 如果尺寸变化或纹理未生成，重新生成
    if (width !== this.lastWidth || height !== this.lastHeight || !this.noiseTexture) {
      this.noiseTexture = generateNoiseTexture(
        width,
        height,
        this.config.scale,
        this.config.density,
        this.config.colored
      )
      this.lastWidth = width
      this.lastHeight = height
    }

    applyNoise(canvas, this.config)
  }

  /**
   * 重置噪点纹理
   */
  reset(): void {
    this.noiseTexture = null
  }
}

// ============================================
// 配置工厂函数
// ============================================

/**
 * 从用户设置创建噪点配置
 */
export function createNoiseConfigFromSettings(
  strength: number,
  scale: number,
  density: number,
  colored: boolean
): NoiseConfig {
  return {
    strength: clamp(strength, 0, 1),
    scale: clamp(Math.round(scale), 1, 10),
    density: clamp(density, 0.1, 1),
    colored,
    time: 0
  }
}

// ============================================
// 默认配置
// ============================================

/** 默认噪点配置 - 参数已调整使效果更明显 */
export const DEFAULT_NOISE_CONFIG: NoiseConfig = {
  strength: 0.5,
  scale: 2,
  density: 0.7,
  colored: false,
  time: 0
}